import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { listarEquipe } from '../../acesso';
import type { Eu, MembroDaEquipe } from '../../acesso';
import type { Sessao } from '../../auth';
import { ErroDeArea } from '../../design/Estados';
import { baixarCsv } from '../../design/csv';
import { Icone } from '../../design/Icone';
import { reais } from '../../produtos';
import { marcarLoteDeOutroProduto } from '../../premiacao';
import {
  ROTULO_DO_PROGRAMA,
  ROTULO_DO_STATUS,
  SIGLA_DO_PROGRAMA,
  cancelarVenda,
  corrigirVenda,
  dataCurta,
  foraDaConta,
  hora,
  lerDetalheDaVenda,
  lerVendasDaEquipe,
  rotuloDaFaixa,
} from '../../vendas';
import type { DetalheDaVenda, ProgramaDeVenda, StatusDaVenda, VendasDaEquipe } from '../../vendas';
import { CartaoDeGrafico } from '../../design/graficos/CartaoDeGrafico';
import { agrupar } from '../../design/graficos/agrupar';
import {
  MODELOS_PARTES,
  MODELOS_TEMPO,
  fmt,
  partes,
  serieNoTempo,
} from '../../design/graficos/modelos';
import '../central/central.css';
import {
  BarraDeFiltros,
  FILTROS_DOS_PROGRAMAS,
  Kpi,
  irPara,
  lerFiltros,
  nomeCurto,
  pctVar,
  textoDoPeriodo,
} from '../paineis/pecasDaVisao';
import './vendas.css';
import { linhaClicavel } from '../../design/linha-clicavel';

/**
 * TELA 09 — VENDAS (visão do admin). "A mesma venda, lida de dois jeitos": para o admin é
 * investigação — filtro, comparação, tabela densa e detalhe com histórico. O status de cada
 * lançamento é o do motor do cheque (o mesmo da Premiação): confirmado, aguarda HOS, não
 * encontrado, dia errado, não premia, cancelada, desfeita.
 */

function hojeBrasilia(): string {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
}
const reaisNum = (s: string) => reais(Number(s).toFixed(2));
const PROGRAMAS: readonly [ProgramaDeVenda, string, string][] = [
  ['vitamina', 'Vitamina', '#1a7f55'],
  ['pre_vencido', 'Pré-vencido', '#cb1b2b'],
  ['paradao', 'Paradão', '#b7791f'],
];
const itensPorDia = (d: VendasDaEquipe) =>
  agrupar(d.porDia.map((x) => ({ dia: x.dia, faturamento: x.itens, cupons: 0, itens: 0 })));

export function Vendas({
  sessao,
  eu,
  parametros,
  programaFixo,
  rota = 'lancamentos',
  cabeca,
}: {
  sessao: Sessao;
  eu: Eu | null;
  parametros: URLSearchParams;
  /** Dentro do módulo de um programa (ex.: Vitaminas › Lançamentos): a tela fica presa nele. */
  programaFixo?: ProgramaDeVenda | undefined;
  rota?: string | undefined;
  /** A subnav do módulo, quando a tela mora dentro dele. */
  cabeca?: ReactNode;
}) {
  const hoje = hojeBrasilia();
  // Dentro do módulo de um programa, o filtro é o dos programas e começa na semana do cheque.
  const padrao = programaFixo ? 'semana' : 'hoje';
  const f = lerFiltros(parametros, hoje, { padrao });
  const periodo = f.periodo;
  const pessoa = f.pessoa ?? '';
  const programa =
    programaFixo ??
    ((PROGRAMAS.some(([x]) => x === parametros.get('programa'))
      ? parametros.get('programa')
      : '') as ProgramaDeVenda | '');
  const status = (
    Object.keys(ROTULO_DO_STATUS).includes(parametros.get('status') ?? '')
      ? parametros.get('status')
      : ''
  ) as StatusDaVenda | '';
  const buscaUrl = (parametros.get('busca') ?? '').slice(0, 80);
  const extras: { programa: string | null; status: string | null; busca: string | null } = {
    programa: programaFixo ? null : programa || null,
    status: status || null,
    busca: buscaUrl || null,
  };
  const ir = (mudar: Partial<typeof extras> & { pessoa?: string | null }) => {
    const { pessoa: novaPessoa, ...resto } = mudar;
    irPara(
      {
        filtro: f.filtro,
        periodo: f.periodo,
        pessoa: novaPessoa === undefined ? f.pessoa : novaPessoa,
      },
      rota,
      { ...extras, ...resto },
      padrao,
    );
  };
  const [busca, setBusca] = useState(buscaUrl);
  useEffect(() => setBusca(buscaUrl), [buscaUrl]);
  const [d, setD] = useState<VendasDaEquipe | null>(null);
  const [erro, setErro] = useState(false);
  const [equipe, setEquipe] = useState<readonly MembroDaEquipe[]>([]);
  const [aberta, setAberta] = useState<string | null>(null);

  useEffect(() => {
    void listarEquipe().then((r) => {
      if (r.ok)
        setEquipe(
          r.dados.pessoas.filter((p) => p.accountId && p.perfis.some((x) => /balconista/i.test(x))),
        );
    });
  }, []);

  const carregar = useCallback(() => {
    setErro(false);
    setD(null);
    void lerVendasDaEquipe({
      de: periodo.de,
      ate: periodo.ate,
      pessoa: pessoa || null,
      programa: programa || null,
      status: status || null,
      busca: buscaUrl,
    }).then((r) => (r.ok ? setD(r.dados) : setErro(true)));
  }, [periodo.de, periodo.ate, pessoa, programa, status, buscaUrl]);
  useEffect(carregar, [carregar]);

  const variacao = (atual: number, antes: number | undefined) =>
    antes === undefined || antes === 0
      ? null
      : pctVar(Math.round(((atual - antes) / antes) * 1000) / 10, 'período anterior');
  const quando = textoDoPeriodo(f.filtro, f.periodo, hoje);
  const nomeDaPessoa = equipe.find((m) => m.accountId === pessoa)?.nome;
  const umDia = periodo.de === periodo.ate;
  const ativas = d ? d.vendas.filter((v) => !foraDaConta(v.status)) : [];
  const porPrograma = PROGRAMAS.map(([id, nome, cor]) => ({
    nome,
    cor,
    itens: ativas.filter((v) => v.programa === id).reduce((s, v) => s + v.quantidade, 0),
    premio: ativas.filter((v) => v.programa === id).reduce((s, v) => s + Number(v.premioTotal), 0),
  }));
  const somaPorFaixa = new Map<string, number>();
  for (const v of ativas) {
    const f = rotuloDaFaixa(v.programa, v.faixa);
    somaPorFaixa.set(f, (somaPorFaixa.get(f) ?? 0) + v.quantidade);
  }
  const porFaixa = [...somaPorFaixa].sort((a, b) => b[1] - a[1]);
  const somaPorPessoa = new Map<string, number>();
  for (const v of ativas)
    somaPorPessoa.set(v.balconista, (somaPorPessoa.get(v.balconista) ?? 0) + v.quantidade);
  const porPessoa = [...somaPorPessoa].sort((a, b) => b[1] - a[1]).slice(0, 10);
  const pico = d ? [...d.porHora].sort((a, b) => b.itens - a.itens)[0] : undefined;
  const topPrograma = [...porPrograma].sort((a, b) => b.itens - a.itens)[0];
  const blocos = d ? itensPorDia(d) : [];
  const melhorDia = [...blocos].sort((a, b) => b.faturamento - a.faturamento)[0];

  return (
    <>
      {cabeca}
      <section className="vd vg">
        <header className="vg-topo">
          <div className="vg-titulo">
            <h1>
              Lançamentos
              {programaFixo ? ` · ${PROGRAMAS.find(([x]) => x === programaFixo)?.[1] ?? ''}` : ''}
            </h1>
            <div className="vg-contexto">
              {programaFixo
                ? `O que os balconistas lançaram de ${(PROGRAMAS.find(([x]) => x === programaFixo)?.[1] ?? '').toLowerCase()}`
                : 'O que os balconistas lançaram nos programas'}{' '}
              · {quando}
              {nomeDaPessoa ? ` · ${nomeDaPessoa}` : ''}
            </div>
          </div>
          <div className="vg-acoes">
            <button
              type="button"
              className="vg-botao"
              onClick={() => exportar(d, periodo)}
              disabled={!d}
            >
              <Icone nome="down" tamanho={14} traco={1.6} />
              Exportar
            </button>
          </div>
        </header>

        <BarraDeFiltros
          f={f}
          hoje={hoje}
          equipe={equipe}
          rota={rota}
          extras={extras}
          padrao={padrao}
          {...(programaFixo ? { filtros: FILTROS_DOS_PROGRAMAS } : {})}
        >
          {!programaFixo && (
            <label className="vg-pessoa">
              <Icone nome="pill" tamanho={14} traco={1.6} />
              <select
                value={programa}
                aria-label="Programa"
                onChange={(e) => ir({ programa: e.target.value || null })}
              >
                <option value="">Todos os programas</option>
                {PROGRAMAS.map(([id, nome]) => (
                  <option key={id} value={id}>
                    {nome}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="vg-pessoa">
            <Icone nome="check" tamanho={14} traco={1.6} />
            <select
              value={status}
              aria-label="Status"
              onChange={(e) => ir({ status: e.target.value || null })}
            >
              <option value="">Todos os status</option>
              {(Object.keys(ROTULO_DO_STATUS) as StatusDaVenda[]).map((x) => (
                <option key={x} value={x}>
                  {ROTULO_DO_STATUS[x]}
                </option>
              ))}
            </select>
          </label>
          <form
            className="cv-busca-nova"
            onSubmit={(e) => {
              e.preventDefault();
              ir({ busca: busca.trim() || null });
            }}
          >
            <Icone nome="search" tamanho={15} traco={1.7} />
            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Produto, EAN, código ou valor (12,90)"
              aria-label="Buscar produto ou EAN"
            />
          </form>
        </BarraDeFiltros>

        {erro ? (
          <ErroDeArea oQue="os lançamentos" aoTentar={carregar} />
        ) : (
          <>
            <div className="vg-kpis">
              <Kpi
                rotulo={`Itens lançados · ${quando}`}
                icone="pill"
                valor={d ? fmt(d.resumo.itens, 'inteiro') : null}
                variacao={d ? variacao(d.resumo.itens, d.anterior?.itens) : null}
                trilha={d?.porDia.map((x) => x.itens)}
                link="#/lancamentos"
                destaque
              />
              <Kpi
                rotulo="Valor vendido"
                icone="money"
                valor={d ? fmt(Number(d.resumo.valorVendido), 'reais') : null}
                variacao={
                  d
                    ? variacao(
                        Number(d.resumo.valorVendido),
                        d.anterior ? Number(d.anterior.valorVendido) : undefined,
                      )
                    : null
                }
                link="#/lancamentos"
              />
              <Kpi
                rotulo="Premiação"
                icone="trophy"
                valor={d ? fmt(Number(d.resumo.premiacao), 'reais') : null}
                variacao={null}
                sub={d ? 'gerada pelos lançamentos' : null}
                link="#/premiacao"
              />
              <Kpi
                rotulo="Média por lançamento"
                icone="receipt"
                valor={
                  d
                    ? d.resumo.mediaPorVenda
                      ? fmt(Number(d.resumo.mediaPorVenda), 'reais')
                      : '—'
                    : null
                }
                variacao={null}
                link="#/lancamentos"
              />
              <Kpi
                rotulo="Balconistas ativos"
                icone="users"
                valor={d ? fmt(d.resumo.balconistas, 'inteiro') : null}
                variacao={null}
                sub={d ? `de ${fmt(d.resumo.balconistasAtivos, 'inteiro')} com acesso` : null}
                link="#/ranking"
              />
              <Kpi
                rotulo="Aguardam HOS"
                icone="clock"
                valor={d ? fmt(d.resumo.aguardandoHos, 'inteiro') : null}
                variacao={null}
                sub={d ? 'lançados, ainda sem a venda do HOS' : null}
                link="#/lancamentos?status=aguarda_hos"
              />
            </div>

            {d && d.vendas.length > 0 && (
              <>
                <div className="vg-linha cv-linha-2">
                  {umDia ? (
                    <CartaoDeGrafico
                      id="vd-hora"
                      titulo={`Lançamentos por horário · ${quando}`}
                      numero={pico ? `pico às ${String(pico.hora)}h` : '—'}
                      conclusao="Itens lançados em cada hora do dia."
                      modelos={MODELOS_TEMPO}
                      padrao="colunas"
                      desenhar={(m) =>
                        serieNoTempo(m, {
                          cats: d.porHora.map((h) => `${String(h.hora).padStart(2, '0')}h`),
                          atual: d.porHora.map((h) => h.itens),
                          anterior: d.porHora.map(() => null),
                          nome: 'Itens',
                          formato: 'inteiro',
                        })
                      }
                      fonte="Fonte: lançamentos do InovaPay"
                      altura={250}
                    />
                  ) : (
                    <CartaoDeGrafico
                      id="vd-evolucao"
                      titulo="Itens lançados por dia"
                      numero={fmt(d.resumo.itens, 'inteiro')}
                      conclusao={
                        melhorDia ? (
                          <>
                            Melhor dia: <strong>{melhorDia.rotulo}</strong> (
                            {fmt(melhorDia.faturamento, 'inteiro')} itens).
                          </>
                        ) : null
                      }
                      modelos={MODELOS_TEMPO}
                      padrao="area"
                      desenhar={(m) =>
                        serieNoTempo(m, {
                          cats: blocos.map((x) => x.rotulo),
                          atual: blocos.map((x) => x.faturamento),
                          anterior: blocos.map(() => null),
                          nome: 'Itens',
                          formato: 'inteiro',
                        })
                      }
                      fonte="Fonte: lançamentos do InovaPay"
                      altura={250}
                    />
                  )}
                  {programaFixo ? (
                    <CartaoDeGrafico
                      id={`vd-faixa-${programaFixo}`}
                      titulo="Por faixa"
                      numero={fmt(Number(d.resumo.premiacao), 'reais')}
                      conclusao={
                        porFaixa[0] ? (
                          <>
                            A faixa mais lançada foi <strong>{porFaixa[0][0]}</strong> (
                            {fmt(porFaixa[0][1], 'inteiro')} itens); o número é o prêmio gerado.
                          </>
                        ) : (
                          'Nenhum item lançado.'
                        )
                      }
                      modelos={MODELOS_PARTES}
                      padrao="rosca"
                      desenhar={(m) =>
                        partes(m, {
                          itens: porFaixa.map(([nome, valor]) => ({ nome, valor })),
                          formato: 'inteiro',
                        })
                      }
                      fonte="Fonte: lançamentos do InovaPay"
                      altura={250}
                    />
                  ) : (
                    <CartaoDeGrafico
                      id="vd-programa"
                      titulo="Por programa"
                      numero={fmt(
                        porPrograma.reduce((s, x) => s + x.premio, 0),
                        'reais',
                      )}
                      conclusao={
                        topPrograma && topPrograma.itens ? (
                          <>
                            <strong>{topPrograma.nome}</strong> teve mais itens (
                            {fmt(topPrograma.itens, 'inteiro')}); o número é a premiação gerada.
                          </>
                        ) : (
                          'Nenhum item lançado.'
                        )
                      }
                      modelos={MODELOS_PARTES}
                      padrao="rosca"
                      desenhar={(m) =>
                        partes(m, {
                          itens: porPrograma
                            .filter((x) => x.itens > 0)
                            .map((x) => ({ nome: x.nome, valor: x.itens, cor: x.cor })),
                          formato: 'inteiro',
                        })
                      }
                      fonte="Fonte: lançamentos do InovaPay"
                      altura={250}
                    />
                  )}
                </div>
                {!pessoa && porPessoa.length > 1 && (
                  <CartaoDeGrafico
                    id="vd-pessoas"
                    titulo="Quem mais lançou"
                    numero={
                      porPessoa[0]
                        ? `${nomeCurto(porPessoa[0][0])} · ${fmt(porPessoa[0][1], 'inteiro')} itens`
                        : '—'
                    }
                    conclusao="Itens lançados por balconista no período, sem os cancelados e desfeitos. Escolha a pessoa no filtro para ver só ela."
                    modelos={MODELOS_PARTES}
                    padrao="colunas"
                    desenhar={(m) =>
                      partes(m, {
                        itens: porPessoa.map(([nome, valor]) => ({
                          nome: nomeCurto(nome),
                          valor,
                        })),
                        formato: 'inteiro',
                      })
                    }
                    fonte="Fonte: lançamentos do InovaPay"
                    altura={240}
                  />
                )}
              </>
            )}

            <div className="vd-tabela">
              <div className="vd-tabela-topo">
                <h2>Lançamentos</h2>
                <span>
                  {d ? `${String(d.vendas.length)} no período` : ''}
                  {nomeDaPessoa ? ` · ${nomeDaPessoa}` : ''}
                </span>
                <div style={{ flex: 1 }} />
                {d && d.resumo.aguardandoHos > 0 && (
                  <span className="vd-chip-ouro">{d.resumo.aguardandoHos} aguardam HOS</span>
                )}
              </div>
              <div className="vd-rolagem">
                <table>
                  <thead>
                    <tr>
                      <th className="vd-esq">HORA</th>
                      <th className="vd-esq">BALCONISTA</th>
                      <th className="vd-esq">PRODUTO</th>
                      <th className="vd-esq">TIPO</th>
                      <th className="vd-esq">FAIXA</th>
                      <th>QTD</th>
                      <th>PREÇO UN.</th>
                      <th>PREMIAÇÃO</th>
                      <th className="vd-esq">STATUS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d !== null && d.vendas.length === 0 && (
                      <tr>
                        <td colSpan={9} className="vd-vazio">
                          Nenhum lançamento com esses filtros.
                        </td>
                      </tr>
                    )}
                    {d?.vendas.map((v) => (
                      <tr
                        key={v.id}
                        {...linhaClicavel(() => setAberta(v.id))}
                        className={`${aberta === v.id ? 'vd-aberta' : ''}${foraDaConta(v.status) ? ' vd-riscada' : ''}`}
                      >
                        <td className="vd-esq vd-mono">
                          {umDia
                            ? hora(v.registradoEm)
                            : `${dataCurta(v.dia).slice(0, 5)} ${hora(v.registradoEm)}`}
                        </td>
                        <td className="vd-esq">{v.balconista}</td>
                        <td className="vd-esq vd-produto">
                          {v.produto}
                          {v.codigo && <span>{v.codigo}</span>}
                        </td>
                        <td className="vd-esq">
                          <span className="vd-sigla">{SIGLA_DO_PROGRAMA[v.programa]}</span>
                        </td>
                        <td className="vd-esq vd-cinza">{rotuloDaFaixa(v.programa, v.faixa)}</td>
                        <td>{v.quantidade}</td>
                        <td>{reaisNum(v.precoUnitario)}</td>
                        <td className="vd-premio">{reaisNum(v.premioTotal)}</td>
                        <td className="vd-esq">
                          <span className={`vd-status vd-status-${v.status}`}>
                            {ROTULO_DO_STATUS[v.status]}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {aberta && (
          <GavetaDaVenda
            id={aberta}
            statusDaLista={d?.vendas.find((x) => x.id === aberta)?.status ?? null}
            sessao={sessao}
            podeCancelar={eu?.capacidades.includes('vendas.ver_todas') ?? false}
            podeCorrigir={eu?.capacidades.includes('premiacao.conferir') ?? false}
            podeMarcarCorte={
              (eu?.capacidades.includes('premiacao.conferir') ?? false) ||
              (eu?.capacidades.includes('auditoria.analisar') ?? false)
            }
            aoFechar={() => setAberta(null)}
            aoMudar={carregar}
            aoVerBalconista={(id) => {
              setAberta(null);
              ir({ pessoa: id });
            }}
          />
        )}
      </section>
    </>
  );
}

function exportar(d: VendasDaEquipe | null, periodo: { de: string; ate: string }) {
  if (!d) return;
  baixarCsv(`vendas-${periodo.de}-a-${periodo.ate}.csv`, [
    [
      'Data',
      'Hora',
      'Balconista',
      'Código',
      'Produto',
      'Tipo',
      'Faixa',
      'Qtd',
      'Preço un.',
      'Premiação',
      'Status',
    ],
    ...d.vendas.map((v) => [
      dataCurta(v.dia),
      hora(v.registradoEm),
      v.balconista,
      v.codigoBalconista,
      v.produto,
      ROTULO_DO_PROGRAMA[v.programa],
      rotuloDaFaixa(v.programa, v.faixa),
      String(v.quantidade),
      v.precoUnitario.replace('.', ','),
      v.premioTotal.replace('.', ','),
      ROTULO_DO_STATUS[v.status],
    ]),
  ]);
}

/* ------------------------------------------------------------------ *
 * Detalhe da venda (gaveta de 460): Resumo · Comprovação · Histórico
 * ------------------------------------------------------------------ */

function GavetaDaVenda({
  id,
  statusDaLista,
  sessao,
  podeCancelar,
  podeCorrigir,
  podeMarcarCorte,
  aoFechar,
  aoMudar,
  aoVerBalconista,
}: {
  id: string;
  statusDaLista: StatusDaVenda | null;
  sessao: Sessao;
  podeCancelar: boolean;
  podeCorrigir: boolean;
  /** Auditor ou quem confere: marca "lote de outro produto" (corta a vitamina do mês). */
  podeMarcarCorte: boolean;
  aoFechar: () => void;
  aoMudar: () => void;
  aoVerBalconista: (accountId: string) => void;
}) {
  const [v, setV] = useState<DetalheDaVenda | null>(null);
  const [erro, setErro] = useState(false);
  const [aba, setAba] = useState<'resumo' | 'prova' | 'historico'>('resumo');
  const [cancelando, setCancelando] = useState(false);
  const [corrigindo, setCorrigindo] = useState(false);
  const [marcando, setMarcando] = useState(false);
  const [detalhe, setDetalhe] = useState('');
  const [marcado, setMarcado] = useState<string | null>(null);
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const carregar = useCallback(() => {
    setErro(false);
    void lerDetalheDaVenda(id).then((r) => {
      if (r.ok) setV(r.dados);
      else setErro(true);
    });
  }, [id]);
  useEffect(carregar, [carregar]);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && aoFechar();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [aoFechar]);

  const calculo = useMemo(() => {
    if (!v) return null;
    const regra =
      v.programa === 'vitamina'
        ? v.faixa === 'a'
          ? 'Faixa MÁX da vitamina'
          : v.faixa === 'b'
            ? 'Faixa MÍN da vitamina'
            : 'Outro valor (régua entre os cards)'
        : v.programa === 'pre_vencido'
          ? 'Pré-vencido pela base'
          : 'Paradão pela base';
    return regra;
  }, [v]);

  return (
    <>
      <div className="vd-veu" onClick={aoFechar} aria-hidden="true" />
      <aside className="vd-gaveta" role="dialog" aria-label="Detalhe da venda">
        <div className="vd-gaveta-topo">
          <div>
            <div className="vd-gaveta-titulo">
              Venda #{id.replace(/-/g, '').slice(-6).toUpperCase()}
            </div>
            <div className="vd-gaveta-sub">
              {v
                ? `${dataCurta(v.dia)} · ${new Date(v.registradoEm).toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo' })}`
                : ' '}
            </div>
          </div>
          <button type="button" className="vd-fechar" onClick={aoFechar} aria-label="Fechar">
            <Icone nome="x" tamanho={16} traco={1.7} />
          </button>
        </div>
        <div className="vd-abas" role="tablist">
          {(
            [
              ['resumo', 'Resumo'],
              ['prova', 'Comprovação'],
              ['historico', 'Histórico'],
            ] as const
          ).map(([k, r]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={aba === k}
              className={aba === k ? 'vd-aba-sel' : ''}
              onClick={() => setAba(k)}
            >
              {r}
            </button>
          ))}
        </div>
        <div className="vd-gaveta-corpo">
          {erro && <ErroDeArea oQue="a venda" aoTentar={carregar} />}
          {v && aba === 'resumo' && (
            <>
              <div className="vd-produto-cab">
                <div>
                  <div className="vd-produto-nome">{v.produto}</div>
                  <div className="vd-produto-meta">
                    {[v.codigo, v.ean, v.fabricante].filter(Boolean).join(' · ')}
                  </div>
                </div>
                {statusDaLista ? (
                  <span className={`vd-status vd-status-${statusDaLista}`}>
                    {ROTULO_DO_STATUS[statusDaLista]}
                  </span>
                ) : (
                  <span
                    className={`vd-status ${v.status === 'cancelado' ? 'vd-status-cancelada' : 'vd-status-confirmado'}`}
                  >
                    {v.status === 'cancelado' ? 'Cancelada' : 'Registrada'}
                  </span>
                )}
              </div>
              <dl className="vd-dados">
                <div>
                  <dt>Registrado por</dt>
                  <dd>
                    {v.balconista} · cód {v.codigoBalconista}
                  </dd>
                </div>
                <div>
                  <dt>Tipo</dt>
                  <dd>{ROTULO_DO_PROGRAMA[v.programa].replace(/^./, (c) => c.toUpperCase())}</dd>
                </div>
                <div>
                  <dt>Faixa escolhida</dt>
                  <dd>{rotuloDaFaixa(v.programa, v.faixa)}</dd>
                </div>
                <div>
                  <dt>Quantidade</dt>
                  <dd>{v.quantidade} un</dd>
                </div>
                <div>
                  <dt>Vendido por</dt>
                  <dd>{reaisNum(v.precoUnitario)} por unidade</dd>
                </div>
              </dl>
              <div className="vd-calculo">
                <div className="vd-calculo-topo">COMO A PREMIAÇÃO FOI CALCULADA</div>
                <div className="vd-calculo-corpo">
                  <div>
                    <span>{calculo}</span>
                    <span>{reaisNum(v.premioUnitario)} / un</span>
                  </div>
                  <div>
                    <span>Quantidade</span>
                    <span>× {v.quantidade}</span>
                  </div>
                  <div className="vd-calculo-total">
                    <span>Premiação da venda</span>
                    <strong>{reaisNum(v.premioTotal)}</strong>
                  </div>
                  {v.explicacao && <p>{v.explicacao}</p>}
                </div>
              </div>
            </>
          )}
          {v && aba === 'prova' && (
            <div className="vd-prova">
              {v.loteCodigo && (
                <p>
                  Lote <strong>{v.loteCodigo}</strong>
                  {v.validade ? ` · validade ${dataCurta(v.validade)}` : ''}
                </p>
              )}
              {v.observacao && <p>Observação: {v.observacao}</p>}
              {v.evidenciaId ? (
                <img src={`/api/evidencias/${v.evidenciaId}`} alt="Foto tirada na hora da venda" />
              ) : (
                <p className="vd-cinza">
                  {v.programa === 'vitamina'
                    ? 'Vitamina não exige comprovação.'
                    : 'Sem foto registrada.'}
                </p>
              )}
            </div>
          )}
          {v && aba === 'historico' && (
            <ul className="vd-historico">
              {v.historico.map((h, i) => (
                <li key={i}>
                  <span>
                    {new Date(h.em).toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      timeZone: 'America/Sao_Paulo',
                    })}
                  </span>
                  {h.texto}
                </li>
              ))}
            </ul>
          )}
          {v && (v.corrigeId ?? v.substituidaPor) && (
            <div className="vd-ligacao">
              {v.corrigeId && (
                <p>
                  Esta venda <b>corrige</b> a venda #{curto(v.corrigeId)}.
                </p>
              )}
              {v.substituidaPor && (
                <p>
                  Substituída pela venda <b>#{curto(v.substituidaPor)}</b> — é a nova que conta na
                  premiação.
                </p>
              )}
            </div>
          )}
          {corrigindo && v && (
            <CorrigirVenda
              v={v}
              sessao={sessao}
              aoVoltar={() => setCorrigindo(false)}
              aoCorrigir={() => {
                setCorrigindo(false);
                aoMudar();
                aoFechar();
              }}
            />
          )}
          {marcado && <div className="vd-cinza">{marcado}</div>}
          {marcando && v && (
            <div className="vd-cancelar">
              <label>
                <span>Lote de outro produto — o que você viu?</span>
                <textarea
                  value={detalhe}
                  onChange={(e) => setDetalhe(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="Ex.: lote L123 / validade 12/26 é da Dipirona, não deste produto"
                  autoFocus
                />
              </label>
              <p className="vd-cinza">
                Regra da loja: quem lança venda com lote de outro produto{' '}
                <b>perde a vitamina do mês inteiro</b>. Fica registrado quem marcou; quem paga a
                premiação pode desfazer, com motivo.
              </p>
              {msg && <div className="vd-erro">{msg}</div>}
              <div className="vd-cancelar-acoes">
                <button
                  type="button"
                  className="vd-botao"
                  onClick={() => setMarcando(false)}
                  disabled={enviando}
                >
                  Voltar
                </button>
                <button
                  type="button"
                  className="vd-botao vd-botao-perigo"
                  disabled={enviando || detalhe.trim().length < 10}
                  onClick={() => {
                    setEnviando(true);
                    setMsg(null);
                    void marcarLoteDeOutroProduto(sessao, v.id, detalhe.trim()).then((r) => {
                      setEnviando(false);
                      if (r.ok) {
                        setMarcando(false);
                        setDetalhe('');
                        setMarcado(
                          'Marcado: a vitamina do mês desta pessoa foi cortada. Aparece na premiação dela.',
                        );
                        aoMudar();
                      } else setMsg(r.mensagem);
                    });
                  }}
                >
                  {enviando ? 'Marcando…' : 'Cortar a vitamina do mês'}
                </button>
              </div>
            </div>
          )}
          {cancelando && v && (
            <div className="vd-cancelar">
              <label>
                <span>Motivo do cancelamento</span>
                <textarea
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  rows={3}
                  maxLength={500}
                  autoFocus
                />
              </label>
              <p className="vd-cinza">
                O motivo aparece para o balconista, na lista dele, e a venda vira desconto na
                premiação da semana.
              </p>
              {msg && <div className="vd-erro">{msg}</div>}
              <div className="vd-cancelar-acoes">
                <button
                  type="button"
                  className="vd-botao"
                  onClick={() => setCancelando(false)}
                  disabled={enviando}
                >
                  Voltar
                </button>
                <button
                  type="button"
                  className="vd-botao vd-botao-perigo"
                  disabled={enviando || motivo.trim().length < 3}
                  onClick={() => {
                    setEnviando(true);
                    setMsg(null);
                    void cancelarVenda(sessao, v.id, motivo.trim()).then((r) => {
                      setEnviando(false);
                      if (r.ok) {
                        setCancelando(false);
                        carregar();
                        aoMudar();
                      } else setMsg(r.mensagem);
                    });
                  }}
                >
                  {enviando ? 'Cancelando…' : 'Cancelar venda'}
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="vd-gaveta-rodape">
          <button
            type="button"
            className="vd-botao-largo"
            onClick={() => v && aoVerBalconista(v.accountId)}
            disabled={!v}
          >
            Ver balconista
          </button>
          {podeCorrigir && v && !v.substituidaPor && !cancelando && !corrigindo && (
            <button type="button" className="vd-botao-largo" onClick={() => setCorrigindo(true)}>
              {v.status === 'ativo' ? 'Corrigir venda' : 'Restaurar venda'}
            </button>
          )}
          {podeMarcarCorte && v && !cancelando && !corrigindo && !marcando && (
            <button type="button" className="vd-botao-largo" onClick={() => setMarcando(true)}>
              Lote de outro produto
            </button>
          )}
          {podeCancelar && v?.status === 'ativo' && !cancelando && !corrigindo && (
            <button
              type="button"
              className="vd-botao-largo vd-botao-perigo"
              onClick={() => setCancelando(true)}
            >
              Cancelar venda
            </button>
          )}
        </div>
      </aside>
    </>
  );
}

const curto = (id: string) => id.replace(/-/g, '').slice(-6).toUpperCase();

/**
 * Corrigir (venda ativa) ou restaurar (cancelada). O servidor recalcula o prêmio pelo mesmo motor
 * da venda; restaurar sem mudar nada devolve a venda com os valores ORIGINAIS.
 */
function CorrigirVenda({
  v,
  sessao,
  aoVoltar,
  aoCorrigir,
}: {
  v: DetalheDaVenda;
  sessao: Sessao;
  aoVoltar: () => void;
  aoCorrigir: () => void;
}) {
  const restaurar = v.status === 'cancelado';
  const [qtd, setQtd] = useState(String(v.quantidade));
  const [faixa, setFaixa] = useState(v.faixa as 'a' | 'b' | 'c' | 'pv');
  const [preco, setPreco] = useState(v.precoUnitario);
  const [dia, setDia] = useState(v.dia);
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const precoLivre = faixa === 'c' || faixa === 'pv';
  const q = Number(qtd);
  const qOk = Number.isInteger(q) && q >= 1 && q <= 9999;
  const precoNum = Number(preco.replace(',', '.'));
  const precoOk = !precoLivre || (Number.isFinite(precoNum) && precoNum > 0);
  const mudou =
    q !== v.quantidade ||
    faixa !== v.faixa ||
    dia !== v.dia ||
    (precoLivre && precoNum !== Number(v.precoUnitario));
  const pronto = qOk && precoOk && motivo.trim().length >= 3 && (restaurar || mudou);
  const enviar = () => {
    setEnviando(true);
    setMsg(null);
    void corrigirVenda(sessao, v.id, {
      ...(q !== v.quantidade ? { quantidade: q } : {}),
      ...(faixa !== v.faixa ? { faixa } : {}),
      ...(precoLivre ? { precoUnitario: precoNum.toFixed(2) } : {}),
      ...(dia !== v.dia ? { dia } : {}),
      motivo: motivo.trim(),
    }).then((r) => {
      setEnviando(false);
      if (r.ok) aoCorrigir();
      else setMsg(r.mensagem);
    });
  };
  return (
    <div className="vd-cancelar vd-corrigir">
      <p className="vd-cinza">
        {restaurar
          ? 'A venda volta a contar na premiação. Sem mudar nada, ela volta com os valores originais.'
          : 'A venda original fica cancelada com o motivo e nasce uma venda nova, com o prêmio recalculado pelo sistema.'}
      </p>
      <div className="vd-corrigir-grade">
        <label>
          <span>Quantidade</span>
          <input
            inputMode="numeric"
            value={qtd}
            onChange={(e) => setQtd(e.target.value.replace(/\D/g, ''))}
          />
        </label>
        {v.programa === 'vitamina' && (
          <label>
            <span>Faixa</span>
            <select value={faixa} onChange={(e) => setFaixa(e.target.value as 'a' | 'b' | 'c')}>
              <option value="a">MÁX</option>
              <option value="b">MÍN</option>
              <option value="c">Outro valor</option>
            </select>
          </label>
        )}
        {precoLivre && (
          <label>
            <span>Preço por unidade (R$)</span>
            <input inputMode="decimal" value={preco} onChange={(e) => setPreco(e.target.value)} />
          </label>
        )}
        <label>
          <span>Dia da venda</span>
          <input
            type="date"
            value={dia}
            max={hojeBrasilia()}
            onChange={(e) => setDia(e.target.value)}
          />
        </label>
      </div>
      <label>
        <span>Motivo {restaurar ? 'da restauração' : 'da correção'}</span>
        <textarea
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          rows={2}
          maxLength={500}
          autoFocus
        />
      </label>
      {msg && <div className="vd-erro">{msg}</div>}
      <div className="vd-cancelar-acoes">
        <button type="button" className="vd-botao" onClick={aoVoltar} disabled={enviando}>
          Voltar
        </button>
        <button
          type="button"
          className="vd-botao vd-botao-principal"
          disabled={!pronto || enviando}
          onClick={enviar}
        >
          {enviando ? 'Salvando…' : restaurar ? 'Restaurar venda' : 'Salvar correção'}
        </button>
      </div>
    </div>
  );
}
